import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Alert, Button, Spin, Typography } from 'antd';
import connectOffice365Service from '../../services/o365/o365Onboarding/connectOffice365.service';
import { O365ConsentStep } from '../../services/o365/o365Onboarding/o365Onboarding.model';

const { Title, Paragraph, Text } = Typography;

const PAGE_TITLE = 'Connect Synapse 360 to Microsoft 365';

type PageState = 'loading' | 'ready' | 'notFound' | 'error';

// Public page (no login) that a client's Microsoft 365 Global Administrator opens from the link an
// admin sends them. It never shows the token, the consent URL or any ids or names.
const ConnectOffice365: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const [state, setState] = useState<PageState>('loading');
  const [connecting, setConnecting] = useState(false);
  const [consentStep, setConsentStep] = useState<O365ConsentStep | null>(null);

  const failureState = (err: any): PageState =>
    err?.response?.status === 404 ? 'notFound' : 'error';

  useEffect(() => {
    const previousTitle = document.title;
    document.title = PAGE_TITLE;
    return () => {
      document.title = previousTitle;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    setState('loading');
    connectOffice365Service
      .resolveLink(token)
      .then((result) => {
        if (cancelled) return;
        setConsentStep(result.consentStep);
        setState('ready');
      })
      .catch((err) => !cancelled && setState(failureState(err)));
    return () => {
      cancelled = true;
    };
  }, [token]);

  // Resolve again on click: each consent URL is minted with a short-lived signed state. The
  // service already refuses anything other than a Microsoft sign-in URL, so a bad response lands
  // in the same error state as any other failure rather than ever reaching window.location.assign.
  const handleConnect = async () => {
    setConnecting(true);
    try {
      const result = await connectOffice365Service.resolveLink(token);
      window.location.assign(result.consentUrl);
    } catch (err) {
      setState(failureState(err));
      setConnecting(false);
    }
  };

  const renderBody = () => {
    switch (state) {
      case 'loading':
        return (
          <div style={{ textAlign: 'center', padding: 40 }}>
            <Spin />
          </div>
        );
      case 'notFound':
        return (
          <Alert
            type="error"
            showIcon
            message="This link isn't valid. Check you copied the whole link, or ask your MetrixData 360 contact for a new one."
          />
        );
      case 'error':
        return (
          <Alert type="error" showIcon message="We couldn't load this page. Please try again later." />
        );
      default:
        return (
          <>
            <Paragraph>
              MetrixData 360 uses the <strong>MetrixData 360 Synapse</strong> app to read your
              organisation&apos;s Microsoft 365 usage reports, licences and users. Access is{' '}
              <strong>read-only</strong>.
            </Paragraph>
            <Alert
              type="warning"
              showIcon
              style={{ marginBottom: 16 }}
              message={
                <>
                  Sign in with an account from <strong>your own organisation</strong> (not a
                  guest account) that holds the <strong>Global Administrator</strong> or{' '}
                  <strong>Privileged Role Administrator</strong> role. If that role is{' '}
                  <strong>PIM-eligible</strong>, activate it before you start.
                </>
              }
            />
            <Paragraph>
              {consentStep === 'sign_in' ? (
                <>You&apos;ll sign in with Microsoft to confirm the organisation you belong to.</>
              ) : (
                <>
                  You&apos;ll sign in with Microsoft. First, approve the app for your organisation
                  (admin consent). A short second sign-in follows, which confirms the organisation
                  you belong to.
                </>
              )}{' '}
              The app asks for these read-only permissions:
            </Paragraph>
            {/* Source of truth: the "MetrixData 360 Synapse" app registration
                (92c382ba-dc87-46d3-a4aa-e090ec0325ab) - update this list if its API permissions change. */}
            <ul>
              <li>Reports.Read.All (usage reports, including Copilot usage)</li>
              <li>User.Read.All (users and deleted users)</li>
              <li>AuditLog.Read.All (sign-in activity)</li>
              <li>Organization.Read.All (licences)</li>
            </ul>
            <div style={{ textAlign: 'center', margin: '24px 0 16px' }}>
              <Button type="primary" size="large" loading={connecting} onClick={handleConnect}>
                {PAGE_TITLE}
              </Button>
            </div>
            <Text type="secondary">If you weren&apos;t expecting this link, close this page.</Text>
          </>
        );
    }
  };

  return (
    <div
      style={{
        minHeight: '100vh',
        backgroundColor: '#00274d',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '40px 16px',
      }}
    >
      <img
        src={`${process.env.PUBLIC_URL}/assets/images/logo.png`}
        alt="MetrixData 360"
        style={{ height: 48, maxWidth: '80%', marginBottom: 24 }}
      />
      <div
        style={{
          background: '#fff',
          borderRadius: 8,
          padding: '32px 36px',
          width: '100%',
          maxWidth: 640,
          boxShadow: '0 8px 24px rgba(0, 0, 0, 0.25)',
        }}
      >
        <Title level={3} style={{ marginTop: 0 }}>
          {PAGE_TITLE}
        </Title>
        {renderBody()}
      </div>
    </div>
  );
};

export default ConnectOffice365;
